/**
 * @fileoverview
 *
 * WidgetWrapper is a HOC which makes sure the Widget component
 * gets properly initialized and commonly needed store attributes are populated.
 *
 * Currently its initialization of zustand store, appContext resolving
 * and store update, fetchedParams resolving and store update.
 *
 * Both redux-zero and zustand are used to initialize the individual stores although
 * redux-zero will be deprecated soon.
 */

import {
  type IAnyWidgetParams,
  type IWidgetInitialParams,
  WidgetTypesEnum,
} from '@core/types'
import { useRegisterFwnWidget } from '@core/widgets/hooks/useRegisterFwnWidget'
import type { AppContextType } from '@embed/components/helpers'

import { WidgetAppContext } from './WidgetAppContext'
import { WidgetExperiments } from './WidgetExperiments'
import { WidgetFeed } from './WidgetFeed'
import { WidgetParams } from './WidgetParams'
import { WidgetServiceApiProvider } from './WidgetServiceApiProvider'
import { WidgetStoreProvider } from './WidgetStoreProvider'
import { WidgetTranslationWrapper } from './WidgetTranslationWrapper'

interface IWidgetWrapperProps {
  appContextType: AppContextType
  initialParams: IWidgetInitialParams
  widgetParams: IAnyWidgetParams
}

/**
 * Inner component to register widget after store is available
 */
const WidgetWrapperInner: React.FunctionComponent<
  React.PropsWithChildren<IWidgetWrapperProps>
> = ({ appContextType, initialParams, widgetParams, children }) => {
  // Register widget for programmatic control via _fwn.widgets
  useRegisterFwnWidget({
    widgetId: initialParams.widget_id ?? '',
    name: widgetParams.name ?? '',
    type: initialParams.type ?? WidgetTypesEnum.UNKNOWN,
  })

  return (
    <WidgetServiceApiProvider>
      <WidgetFeed appContextType={appContextType}>
        <WidgetAppContext appContextType={appContextType}>
          <WidgetParams>
            <WidgetExperiments>
              <WidgetTranslationWrapper>{children}</WidgetTranslationWrapper>
            </WidgetExperiments>
          </WidgetParams>
        </WidgetAppContext>
      </WidgetFeed>
    </WidgetServiceApiProvider>
  )
}

/**
 *
 */
export const WidgetWrapper: React.FunctionComponent<
  React.PropsWithChildren<IWidgetWrapperProps>
> = ({ appContextType, initialParams, widgetParams, children }) => {
  return (
    <WidgetStoreProvider
      initialParams={initialParams}
      widgetParams={widgetParams}
    >
      <WidgetWrapperInner
        appContextType={appContextType}
        initialParams={initialParams}
        widgetParams={widgetParams}
      >
        {children}
      </WidgetWrapperInner>
    </WidgetStoreProvider>
  )
}
